import { useRef, useState } from 'react';
import { Inbox, Mic, HardDrive, FileText, Trash2, Plus, ExternalLink, Upload, Search, Pencil, Download, FolderInput } from 'lucide-react';
import type { ContextFolder } from '@warriorlab/shared';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import { Input, Field } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { ListRow } from '@/components/ui/ListRow';
import { SidePanel } from '@/components/ui/SidePanel';
import { Markdown } from '@/components/ui/Markdown';
import { EmptyState, ErrorState, Spinner } from '@/components/ui/States';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { confirm } from '@/components/ui/ConfirmDialog';
import { toast } from '@/components/ui/Toast';
import { cn } from '@/lib/cn';
import { useAsync } from '@/lib/useAsync';
import { timeAgo } from '@/lib/timeAgo';
import { api, ApiError, type ContextItemSummary } from '@/lib/api';
import { useWorkspaceCtx } from './WorkspaceLayout';

const FOLDERS: { key: ContextFolder | 'ALL'; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'MEETINGS', label: 'Meetings' },
  { key: 'SURVEYS', label: 'Surveys' },
  { key: 'DATA', label: 'Data' },
];

const sourceIcon = (item: ContextItemSummary) =>
  item.kind === 'FIREFLIES' ? <Mic className="h-4 w-4" /> : item.kind === 'GOOGLE_DRIVE' ? <HardDrive className="h-4 w-4" /> : <FileText className="h-4 w-4" />;

type ContextSort = 'newest' | 'oldest' | 'name';
type RoutingFilter = 'ALL' | 'PENDING' | 'DISTRIBUTED';

const routingOf = (i: ContextItemSummary): Exclude<RoutingFilter, 'ALL'> | null => {
  const r = (i.meta as { routing?: { pending?: boolean; distributed?: string[] } } | null)?.routing;
  if (r?.pending) return 'PENDING';
  if (r?.distributed?.length) return 'DISTRIBUTED';
  return null;
};

/** Staff-only: the raw engagement evidence. Clients never see this tab. */
export function WorkspaceContextTab() {
  const { workspace } = useWorkspaceCtx();
  const items = useAsync(() => api.listContext(workspace.id), [workspace.id]);
  const [folder, setFolder] = useState<ContextFolder | 'ALL'>('ALL');
  const [routing, setRouting] = useState<RoutingFilter>('ALL');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<ContextSort>('newest');
  const [openId, setOpenId] = useState<string | null>(null);
  const [editItem, setEditItem] = useState<ContextItemSummary | null>(null);
  const [movingItem, setMovingItem] = useState<ContextItemSummary | null>(null);
  const [adding, setAdding] = useState(false);

  async function remove(item: ContextItemSummary) {
    const ok = await confirm({
      title: `Delete “${item.title}”?`,
      description: 'Agents lose this evidence on their next run. Deliverables that cited it keep their citations.',
    });
    if (!ok) return;
    try {
      await api.deleteContextItem(item.id);
      toast.success('Context item deleted');
      items.reload();
    } catch (err) {
      toast.danger('Could not delete', err instanceof ApiError ? err.message : undefined);
    }
  }

  const all = items.status === 'success' ? items.data : [];
  const q = query.trim().toLowerCase();
  const searched = all.filter(
    (i) =>
      !q ||
      i.title.toLowerCase().includes(q) ||
      i.fileName.toLowerCase().includes(q) ||
      i.tags.some((t) => t.toLowerCase().includes(q)),
  );
  const sorted = [...searched].sort((a, b) =>
    sort === 'name'
      ? a.title.localeCompare(b.title)
      : sort === 'oldest'
        ? a.ingestedAt.localeCompare(b.ingestedAt)
        : b.ingestedAt.localeCompare(a.ingestedAt),
  );
  const visible = sorted.filter(
    (i) => (folder === 'ALL' || i.folder === folder) && (routing === 'ALL' || routingOf(i) === routing),
  );
  const countFor = (key: ContextFolder | 'ALL') =>
    key === 'ALL' ? searched.length : searched.filter((i) => i.folder === key).length;
  const pendingCount = searched.filter((i) => routingOf(i) === 'PENDING').length;
  const distributedCount = searched.filter((i) => routingOf(i) === 'DISTRIBUTED').length;

  const renderRow = (item: ContextItemSummary) => (
    <ListRow
      key={item.id}
      icon={sourceIcon(item)}
      title={item.title}
      meta={
        `${item.fileName} · ${item.folder.toLowerCase()} · ${timeAgo(item.ingestedAt)}` +
        (item.tags.length ? ` · ${item.tags.map((t) => `#${t}`).join(' ')}` : '')
      }
      actions={
        <>
          {item.url && (
            <a href={item.url} target="_blank" rel="noreferrer" className="text-fg-subtle hover:text-fg" aria-label="Open source">
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
          {item.file && (
            <a
              href={api.contextFileUrl(item.id)}
              className="text-fg-subtle hover:text-fg"
              aria-label={`Download ${item.file.originalName}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Download className="h-4 w-4" />
            </a>
          )}
          {(() => {
            const routing = (item.meta as { routing?: { pending?: boolean; distributed?: string[]; distributedFrom?: string } } | null)?.routing;
            if (routing?.pending) return <Badge tone="warning">needs routing</Badge>;
            if (routing?.distributedFrom) return <Badge tone="neutral">copy</Badge>;
            if (routing?.distributed?.length) return <Badge tone="success">distributed ×{routing.distributed.length}</Badge>;
            return null;
          })()}
          <Badge tone="neutral">{item.kind ? item.kind.replace('_', ' ').toLowerCase() : 'manual'}</Badge>
          <IconButton label="Distribute to workspaces" onClick={(e) => { e.stopPropagation(); setMovingItem(item); }}>
            <FolderInput className="h-4 w-4" />
          </IconButton>
          <IconButton label="Rename or tag" onClick={(e) => { e.stopPropagation(); setEditItem(item); }}>
            <Pencil className="h-4 w-4" />
          </IconButton>
          <IconButton label="Delete" onClick={(e) => { e.stopPropagation(); remove(item); }}>
            <Trash2 className="h-4 w-4" />
          </IconButton>
        </>
      }
      onClick={() => setOpenId(item.id)}
    />
  );

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search title or file name"
            className="w-60 pl-8"
          />
        </div>
        <Select value={sort} onChange={(e) => setSort(e.target.value as ContextSort)} className="w-auto">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="name">By name</option>
        </Select>
        <Button size="sm" className="ml-auto" onClick={() => setAdding(true)}>
          <Plus className="h-4 w-4" /> Add context
        </Button>
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        {FOLDERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFolder(f.key)}
            className={cn(
              'rounded-full border px-3 py-1 text-xs font-medium transition',
              folder === f.key ? 'border-primary bg-primary-muted text-primary' : 'border-border text-fg-muted hover:text-fg',
            )}
          >
            {f.label} <span className="tabular-nums opacity-70">{countFor(f.key)}</span>
          </button>
        ))}
        {(pendingCount > 0 || distributedCount > 0) && <span className="mx-1 h-4 w-px bg-border" aria-hidden />}
        {([
          ['PENDING', 'Needs routing', pendingCount],
          ['DISTRIBUTED', 'Distributed', distributedCount],
        ] as const)
          .filter(([, , n]) => n > 0)
          .map(([key, label, n]) => (
            <button
              key={key}
              onClick={() => setRouting(routing === key ? 'ALL' : key)}
              className={cn(
                'rounded-full border px-3 py-1 text-xs font-medium transition',
                routing === key ? 'border-primary bg-primary-muted text-primary' : 'border-border text-fg-muted hover:text-fg',
              )}
            >
              {label} <span className="tabular-nums opacity-70">{n}</span>
            </button>
          ))}
        <span className="ml-auto text-xs text-fg-subtle">
          {visible.length === searched.length
            ? `${searched.length} item${searched.length === 1 ? '' : 's'}`
            : `${visible.length} of ${searched.length} items`}
        </span>
      </div>

      {items.status === 'loading' && <SkeletonRows rows={4} />}
      {items.status === 'error' && <ErrorState error={items.error} onRetry={items.reload} />}
      {items.status === 'success' && visible.length === 0 && (
        <EmptyState
          icon={Inbox}
          title={q ? 'No matches' : folder === 'ALL' ? 'No context yet' : 'Nothing in this folder'}
          description={q ? 'Try a different search.' : 'Connector syncs and manual uploads land here.'}
        />
      )}
      {items.status === 'success' && visible.length > 0 && folder === 'ALL' && (
        <div className="space-y-6">
          {FOLDERS.filter((f) => f.key !== 'ALL').map((f) => {
            const group = visible.filter((i) => i.folder === f.key);
            if (group.length === 0) return null;
            return (
              <section key={f.key}>
                <h3 className="mb-1 text-sm font-semibold">
                  {f.label} <span className="text-xs font-normal text-fg-subtle">{group.length}</span>
                </h3>
                <ul className="divide-y divide-border">{group.map(renderRow)}</ul>
              </section>
            );
          })}
        </div>
      )}
      {items.status === 'success' && visible.length > 0 && folder !== 'ALL' && (
        <ul className="divide-y divide-border">{visible.map(renderRow)}</ul>
      )}

      {openId && <ContextReader itemId={openId} onClose={() => setOpenId(null)} />}
      {movingItem && (
        <ContextMovePanel
          item={movingItem}
          currentWorkspaceId={workspace.id}
          onClose={() => setMovingItem(null)}
          onMoved={() => {
            setMovingItem(null);
            items.reload();
          }}
        />
      )}
      {editItem && (
        <ContextEditPanel
          item={editItem}
          onClose={() => setEditItem(null)}
          onSaved={() => {
            setEditItem(null);
            items.reload();
          }}
        />
      )}
      {adding && (
        <ContextUploadPanel
          workspaceId={workspace.id}
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            items.reload();
          }}
        />
      )}
    </>
  );
}

function ContextReader({ itemId, onClose }: { itemId: string; onClose: () => void }) {
  const item = useAsync(() => api.getContextItem(itemId), [itemId]);
  return (
    <SidePanel
      title={item.status === 'success' ? item.data.title : 'Context item'}
      subtitle={item.status === 'success' ? `${item.data.fileName} · ${timeAgo(item.data.ingestedAt)}` : undefined}
      onClose={onClose}
    >
      {item.status === 'loading' && <Spinner label="Loading…" />}
      {item.status === 'error' && <ErrorState error={item.error} onRetry={item.reload} />}
      {item.status === 'success' && <Markdown className="text-sm">{item.data.content || '_(empty)_'}</Markdown>}
    </SidePanel>
  );
}

/** Rename, recategorise, and tag an item. Edits display metadata only; the
 * file name (the citation key) is shown but never editable. */
function ContextEditPanel({ item, onClose, onSaved }: { item: ContextItemSummary; onClose: () => void; onSaved: () => void }) {
  const [title, setTitle] = useState(item.title);
  const [folder, setFolder] = useState<ContextFolder>(item.folder);
  const [tagsText, setTagsText] = useState(item.tags.join(', '));
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const tags = tagsText.split(',').map((t) => t.trim()).filter(Boolean);
    try {
      await api.updateContextItem(item.id, { title: title.trim(), folder, tags });
      toast.success('Context updated');
      onSaved();
    } catch (err) {
      toast.danger('Could not update', err instanceof ApiError ? err.message : undefined);
      setBusy(false);
    }
  }

  return (
    <SidePanel title="Edit context" subtitle={item.fileName} onClose={onClose}>
      <form className="space-y-4" onSubmit={submit}>
        <Field label="Title" htmlFor="ctx-edit-title" hint="Display name only. The file name (citation key) never changes.">
          <Input id="ctx-edit-title" value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />
        </Field>
        <Field label="Folder" htmlFor="ctx-edit-folder">
          <Select id="ctx-edit-folder" value={folder} onChange={(e) => setFolder(e.target.value as ContextFolder)}>
            <option value="MEETINGS">Meetings</option>
            <option value="SURVEYS">Surveys</option>
            <option value="DATA">Data</option>
          </Select>
        </Field>
        <Field label="Tags" htmlFor="ctx-edit-tags" hint="Comma-separated labels for organising and searching.">
          <Input
            id="ctx-edit-tags"
            value={tagsText}
            onChange={(e) => setTagsText(e.target.value)}
            placeholder="e.g. kickoff, hvp, follow-up"
          />
        </Field>
        <Button type="submit" disabled={busy || !title.trim()}>
          {busy ? 'Saving…' : 'Save changes'}
        </Button>
      </form>
    </SidePanel>
  );
}

/** Distribute an item to one or more workspaces. The master stays put (one
 * global file); each selected workspace receives a linked copy. Rule-matched
 * suggestions come pre-selected. Staff-only (the tab already is). */
function ContextMovePanel({
  item,
  currentWorkspaceId,
  onClose,
  onMoved,
}: {
  item: ContextItemSummary;
  currentWorkspaceId: string;
  onClose: () => void;
  onMoved: () => void;
}) {
  const suggestions = useAsync(async () => {
    const s = await api.routeSuggestions(item.id);
    setSelected(new Set(s.map((x) => x.workspaceId)));
    return s;
  }, [item.id]);
  const workspaces = useAsync(() => api.listAllWorkspaces(), []);
  const companies = useAsync(() => api.listCompanies(), []);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [creatingFor, setCreatingFor] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function distribute() {
    setBusy(true);
    try {
      await api.distributeContextItem(item.id, [...selected]);
      toast.success(`Distributed to ${selected.size} workspace${selected.size === 1 ? '' : 's'}`, 'The original stays here.');
      onMoved();
    } catch (err) {
      toast.danger('Could not distribute the item', err instanceof ApiError ? err.message : undefined);
      setBusy(false);
    }
  }

  async function createIn(company: { id: string; name: string }) {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const ws = await api.createWorkspace(company.id, name);
      setSelected((prev) => new Set(prev).add(ws.id));
      setCreatingFor(null);
      setNewName('');
      toast.success(`Workspace “${ws.name}” created in ${company.name}`, 'Selected for distribution.');
      workspaces.reload();
    } catch (err) {
      toast.danger('Could not create the workspace', err instanceof ApiError ? err.message : undefined);
    } finally {
      setCreating(false);
    }
  }

  /** Filing shouldn't force a detour: a company that doesn't exist yet is
   * created here, then its first workspace inline via the same flow. */
  async function createCompany(name: string) {
    setCreating(true);
    try {
      const company = await api.createCompany(name);
      toast.success(`Company “${company.name}” created`, 'Now add its first workspace.');
      setCreatingFor(company.id);
      setNewName('');
      companies.reload();
    } catch (err) {
      toast.danger('Could not create the company', err instanceof ApiError ? err.message : undefined);
    } finally {
      setCreating(false);
    }
  }

  const q = query.trim().toLowerCase();
  const matches = (workspaceName: string, companyName: string) =>
    !q || workspaceName.toLowerCase().includes(q) || companyName.toLowerCase().includes(q);
  const suggested = (suggestions.status === 'success' ? suggestions.data : []).filter((s) =>
    matches(s.workspace.name, s.workspace.company.name),
  );
  const suggestedIds = new Set(suggested.map((s) => s.workspaceId));
  const all = workspaces.status === 'success' ? workspaces.data : [];
  const wsByCompany = new Map<string, typeof all>();
  for (const w of all) {
    if (w.id === currentWorkspaceId || suggestedIds.has(w.id)) continue;
    const list = wsByCompany.get(w.company.id) ?? [];
    list.push(w);
    wsByCompany.set(w.company.id, list);
  }
  // Companies drive the grouping so a company with no workspaces yet still
  // appears and can have its first workspace created inline.
  const groups = (companies.status === 'success' ? companies.data : [])
    .map((c) => {
      const ws = wsByCompany.get(c.id) ?? [];
      const companyHit = !q || c.name.toLowerCase().includes(q);
      return {
        company: c,
        workspaces: companyHit ? ws : ws.filter((w) => w.name.toLowerCase().includes(q)),
        companyHit,
      };
    })
    .filter((g) => g.companyHit || g.workspaces.length > 0);
  // Offer creation whenever the search names a company that doesn't exist.
  const newCompanyName = query.trim();
  const canCreateCompany =
    newCompanyName.length > 0 &&
    companies.status === 'success' &&
    !companies.data.some((c) => c.name.toLowerCase() === newCompanyName.toLowerCase());

  const option = (id: string, label: string, sub?: string) => (
    <label
      key={id}
      className={cn(
        'flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2 transition',
        selected.has(id) ? 'border-primary bg-primary-muted/40' : 'border-border hover:border-primary/40',
      )}
    >
      <input
        type="checkbox"
        checked={selected.has(id)}
        onChange={() => toggle(id)}
        className="mt-1 h-3.5 w-3.5"
      />
      <span className="text-sm text-fg">
        {label}
        {sub && <span className="mt-0.5 block text-xs text-fg-subtle">{sub}</span>}
      </span>
    </label>
  );

  return (
    <SidePanel
      title="Distribute to workspaces"
      subtitle={`${item.title} — the original stays in this workspace; selected workspaces get a copy.`}
      onClose={onClose}
    >
      {(suggestions.status === 'loading' || workspaces.status === 'loading') && <Spinner label="Loading workspaces…" />}
      {workspaces.status === 'error' && <ErrorState error={workspaces.error} onRetry={workspaces.reload} />}
      {workspaces.status === 'success' && (
        <div className="space-y-5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search companies and workspaces"
              className="w-full pl-8"
              autoFocus
            />
          </div>
          {suggested.length > 0 && (
            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-subtle">Suggested by routing rules</h3>
              <div className="space-y-2">
                {suggested.map((s) =>
                  option(s.workspaceId, `${s.workspace.company.name} · ${s.workspace.name}`, `matched: ${s.hits.join(', ')}`),
                )}
              </div>
            </div>
          )}
          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-subtle">All companies</h3>
            <div className="max-h-80 space-y-3 overflow-y-auto pr-1">
              {groups.map((g) => (
                <div key={g.company.id}>
                  <div className="mb-1 text-xs font-medium text-fg-muted">{g.company.name}</div>
                  <div className="space-y-2">
                    {g.workspaces.map((w) => option(w.id, w.name))}
                    {creatingFor === g.company.id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                          placeholder="New workspace name"
                          className="h-8 flex-1 text-sm"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void createIn(g.company);
                            }
                          }}
                        />
                        <Button size="sm" disabled={creating || !newName.trim()} onClick={() => void createIn(g.company)}>
                          {creating ? 'Creating…' : 'Create'}
                        </Button>
                        <button
                          type="button"
                          className="text-xs text-fg-subtle transition hover:text-fg"
                          onClick={() => setCreatingFor(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setCreatingFor(g.company.id);
                          setNewName('');
                        }}
                        className="flex items-center gap-1 text-xs font-medium text-fg-subtle transition hover:text-fg"
                      >
                        <Plus className="h-3.5 w-3.5" /> New workspace
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {canCreateCompany && (
                <button
                  type="button"
                  disabled={creating}
                  onClick={() => void createCompany(newCompanyName)}
                  className="flex items-center gap-1 text-xs font-medium text-fg-subtle transition hover:text-fg disabled:opacity-50"
                >
                  <Plus className="h-3.5 w-3.5" /> New company “{newCompanyName}”
                </button>
              )}
              {groups.length === 0 && suggested.length === 0 && !canCreateCompany && (
                <p className="text-sm text-fg-subtle">No companies match “{query.trim()}”.</p>
              )}
            </div>
          </div>
          <Button onClick={distribute} disabled={busy || selected.size === 0}>
            {busy ? 'Distributing…' : `Distribute to ${selected.size || '…'}`}
          </Button>
        </div>
      )}
    </SidePanel>
  );
}

const UPLOAD_EXTENSIONS = ['.pdf', '.docx', '.md', '.txt', '.csv', '.json'];
const MAX_FILE_BYTES = 15 * 1024 * 1024;
const isSupported = (f: File) => UPLOAD_EXTENSIONS.some((ext) => f.name.toLowerCase().endsWith(ext));

function ContextUploadPanel({ workspaceId, onClose, onSaved }: { workspaceId: string; onClose: () => void; onSaved: () => void }) {
  const [title, setTitle] = useState('');
  const [folder, setFolder] = useState<ContextFolder>('DATA');
  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [binaryNote, setBinaryNote] = useState(false);
  /** Files chosen but not yet uploaded — nothing leaves the browser until "Upload" is confirmed. */
  const [staged, setStaged] = useState<File[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.createContextItem(workspaceId, { title: title.trim(), content, folder });
      toast.success('Context added');
      onSaved();
    } catch (err) {
      toast.danger('Could not add context', err instanceof ApiError ? err.message : undefined);
      setBusy(false);
    }
  }

  /** Materials drop: files are STAGED for review first — a mis-picked file can
   * be removed before anything uploads. Text is extracted server-side on
   * upload and the original kept for download. */
  function handleFiles(list: FileList | File[]) {
    const files = [...list];
    const unsupported = files.filter((f) => !isSupported(f));
    const oversize = files.filter((f) => isSupported(f) && f.size > MAX_FILE_BYTES);
    const usable = files.filter((f) => isSupported(f) && f.size <= MAX_FILE_BYTES);
    setBinaryNote(unsupported.length > 0);
    if (oversize.length > 0) toast.warning('Files too large', 'Each file must be 15 MB or less.');
    if (usable.length === 0) return;
    setStaged((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
      return [...prev, ...usable.filter((f) => !seen.has(`${f.name}:${f.size}`))];
    });
  }

  async function uploadStaged() {
    if (staged.length === 0) return;
    setBusy(true);
    const failed: string[] = [];
    for (const file of staged) {
      try {
        await api.uploadContextFile(workspaceId, file, folder);
      } catch (err) {
        failed.push(err instanceof ApiError ? `${file.name}: ${err.message}` : file.name);
      }
    }
    setBusy(false);
    const ok = staged.length - failed.length;
    if (failed.length > 0) {
      setStaged(staged.filter((f) => failed.some((msg) => msg.startsWith(f.name))));
      toast.warning(`Added ${ok} of ${staged.length} file(s)`, `Failed: ${failed.join(', ')}`);
    } else {
      toast.success(`Added ${ok} file(s)`);
    }
    if (ok > 0 && failed.length === 0) onSaved();
  }

  const fmtSize = (bytes: number) =>
    bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

  return (
    <SidePanel title="Add context" subtitle="Upload files or paste text." onClose={onClose}>
      {/* Materials drop zone — text files become items directly. */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => fileInput.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') fileInput.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          void handleFiles(e.dataTransfer.files);
        }}
        className={cn(
          'mb-5 flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed px-4 py-8 text-center transition',
          dragOver ? 'border-primary bg-primary-muted/50' : 'border-border-strong hover:border-primary/50',
        )}
      >
        <Upload className="h-5 w-5 text-fg-subtle" aria-hidden />
        <p className="mt-2 text-sm font-medium text-fg">{busy ? 'Uploading…' : 'Drop files or click to browse'}</p>
        <p className="mt-1 text-xs text-fg-subtle">
          .pdf .docx .md .txt .csv .json, up to 15 MB each. Nothing uploads until you confirm below.
        </p>
        {binaryNote && (
          <p className="mt-2 rounded-lg bg-warning/10 px-2.5 py-1 text-xs text-warning">
            Some files were skipped — only PDF, Word (.docx) and text formats are supported.
          </p>
        )}
        <input
          ref={fileInput}
          type="file"
          multiple
          accept={UPLOAD_EXTENSIONS.join(',')}
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {/* Confirmation layer: review the picked files before anything uploads. */}
      {staged.length > 0 && (
        <div className="mb-5 rounded-xl border border-border p-3">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-subtle">
            Ready to upload ({staged.length})
          </h4>
          <ul className="divide-y divide-border">
            {staged.map((f) => (
              <li key={`${f.name}:${f.size}`} className="flex items-center gap-2.5 py-1.5">
                <FileText className="h-4 w-4 shrink-0 text-fg-subtle" aria-hidden />
                <span className="min-w-0 flex-1 truncate text-sm text-fg" title={f.name}>
                  {f.name}
                </span>
                <span className="shrink-0 text-xs tabular-nums text-fg-subtle">{fmtSize(f.size)}</span>
                <IconButton
                  label={`Remove ${f.name}`}
                  onClick={() => setStaged((prev) => prev.filter((s) => s !== f))}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </IconButton>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex items-center justify-between gap-2">
            <span className="text-xs text-fg-subtle">Into {folder.toLowerCase()} (change in the folder field below).</span>
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" disabled={busy} onClick={() => setStaged([])}>
                Clear
              </Button>
              <Button size="sm" disabled={busy} onClick={() => void uploadStaged()}>
                {busy ? 'Uploading…' : `Upload ${staged.length} file${staged.length === 1 ? '' : 's'}`}
              </Button>
            </div>
          </div>
        </div>
      )}

      <form id="context-upload" className="space-y-4" onSubmit={submit}>
        <Field label="Title" htmlFor="ctx-title">
          <Input id="ctx-title" value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />
        </Field>
        <Field label="Folder" htmlFor="ctx-folder">
          <Select id="ctx-folder" value={folder} onChange={(e) => setFolder(e.target.value as ContextFolder)}>
            <option value="MEETINGS">Meetings</option>
            <option value="SURVEYS">Surveys</option>
            <option value="DATA">Data</option>
          </Select>
        </Field>
        <Field label="Content" htmlFor="ctx-content" hint="Markdown or plain text.">
          <textarea
            id="ctx-content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={14}
            required
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm leading-relaxed text-fg placeholder:text-fg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          />
        </Field>
        <Button type="submit" disabled={busy || !title.trim() || !content.trim()}>
          {busy ? 'Adding…' : 'Add context'}
        </Button>
      </form>
    </SidePanel>
  );
}
